import { DRAWING_EDGE_LABEL_STYLE, DRAWING_STYLE } from '../styles';
import { toBdPoint } from '../geometry';
import {
  createDrawingPointOverlay,
  createSnapRingOverlay,
} from '../overlays/drawing';

/**
 * 绘制渲染器
 *
 * 层级：map-core（"地图怎么动"）
 *
 * 职责：
 * 1. 渲染已确认顶点之间的实线（templine）
 * 2. 渲染最后一个顶点到光标的虚线提示（hintline），自相交时变红
 * 3. 渲染半透明预览填充面（preview polygon）
 * 4. 渲染顶点蓝色圆点 Marker 与吸附环
 *
 * 不维护绘制状态，只根据传入的点/光标坐标做视觉呈现
 */
export class DrawingRenderer {
  private templine: any = null;
  private hintline: any = null;
  private previewPolygon: any = null;
  private markers: Array<{ overlay: any; remove: () => void }> = [];
  private snapRing: { overlay: any; remove: () => void } | null = null;
  private edgeLabels: Array<{ overlay: any; remove: () => void }> = [];
  /** 绘制期样式覆盖（用于轻图测试等需要按子类型着色的场景） */
  private styleOverride: Partial<{
    lineColor: string;
    polygonFill: string;
    polygonStroke: string;
    pointFill: string;
  }> = {};

  constructor(private map: any) {}

  /** 设置绘制期样式覆盖，传入空对象恢复默认 */
  setStyleOverride(override: Partial<{
    lineColor: string;
    polygonFill: string;
    polygonStroke: string;
    pointFill: string;
  }>) {
    this.styleOverride = override;
  }

  /** 添加一个顶点 Marker */
  addVertexMarker(point: [number, number]) {
    const marker = createDrawingPointOverlay(
      this.map,
      toBdPoint(point),
      this.styleOverride.pointFill
    );
    this.markers.push(marker);
  }

  /** 移除最后一个顶点 Marker */
  removeLastVertexMarker() {
    const marker = this.markers.pop();
    marker?.remove?.();
  }

  /** 更新已确认顶点之间的实线 */
  updateTempline(points: [number, number][]) {
    if (points.length < 2) return;

    const bdPoints = points.map((p) => toBdPoint(p));
    const lineColor = this.styleOverride.lineColor ?? DRAWING_STYLE.line.strokeColor;
    if (!this.templine) {
      this.templine = new window.BMapGL.Polyline(bdPoints, {
        strokeColor: lineColor,
        strokeWeight: DRAWING_STYLE.line.strokeWeight,
        strokeOpacity: DRAWING_STYLE.line.strokeOpacity,
        enableParse: false,
        enableClicking: false,
      });
      this.map.addOverlay(this.templine);
    } else {
      this.templine.setPath(bdPoints);
      this.templine.setStrokeColor(lineColor);
    }
  }

  /**
   * 更新从最后一个顶点到光标的虚线
   * @param from 最后一个顶点
   * @param cursor 光标坐标
   * @param isIntersecting 是否会导致自相交（变红）
   */
  updateHintline(
    from: [number, number],
    cursor: [number, number],
    isIntersecting: boolean
  ) {
    const path = [toBdPoint(from), toBdPoint(cursor)];
    const defaultLineColor = this.styleOverride.lineColor ?? DRAWING_STYLE.line.strokeColor;
    const color = isIntersecting
      ? DRAWING_STYLE.line.errorColor
      : defaultLineColor;

    if (!this.hintline) {
      this.hintline = new window.BMapGL.Polyline(path, {
        strokeColor: color,
        strokeWeight: DRAWING_STYLE.line.strokeWeight,
        strokeOpacity: 0.6,
        strokeStyle: 'dashed',
        enableClicking: false,
      });
      this.map.addOverlay(this.hintline);
    } else {
      this.hintline.setPath(path);
      this.hintline.setStrokeColor(color);
    }
  }

  /** 清除 hintline */
  clearHintline() {
    if (this.hintline) {
      this.map.removeOverlay(this.hintline);
      this.hintline = null;
    }
  }

  /**
   * 更新半透明预览填充面
   * @param points 已确认顶点（不含 cursor）
   * @param cursor 光标坐标
   * @param shouldRender 是否允许渲染（例如自相交时不渲染）
   */
  updatePreviewPolygon(
    points: [number, number][],
    cursor: [number, number],
    shouldRender: boolean
  ) {
    if (!shouldRender || points.length < 2) {
      this.clearPreviewPolygon();
      return;
    }

    const previewPoints = [...points, cursor];
    const bdPoints = previewPoints.map((p) => toBdPoint(p));

    const polygonStroke = this.styleOverride.polygonStroke ?? DRAWING_STYLE.polygon.strokeColor;
    const polygonFill = this.styleOverride.polygonFill ?? DRAWING_STYLE.polygon.fillColor;

    if (!this.previewPolygon) {
      this.previewPolygon = new window.BMapGL.Polygon(bdPoints, {
        strokeColor: polygonStroke,
        strokeWeight: DRAWING_STYLE.polygon.strokeWeight,
        strokeOpacity: DRAWING_STYLE.polygon.strokeOpacity,
        fillColor: polygonFill,
        fillOpacity: DRAWING_STYLE.polygon.fillOpacity,
        enableParse: false,
        enableClicking: false,
      });
      this.map.addOverlay(this.previewPolygon);
    } else {
      this.previewPolygon.setPath(bdPoints);
      this.previewPolygon.setStrokeColor(polygonStroke);
      this.previewPolygon.setFillColor(polygonFill);
    }
  }

  /** 清除预览面 */
  clearPreviewPolygon() {
    if (this.previewPolygon) {
      this.map.removeOverlay(this.previewPolygon);
      this.previewPolygon = null;
    }
  }

  /** 显示吸附环 */
  showSnapRing(point: [number, number]) {
    if (this.snapRing) return;
    this.snapRing = createSnapRingOverlay(this.map, toBdPoint(point));
  }

  /** 隐藏吸附环 */
  hideSnapRing() {
    if (!this.snapRing) return;
    this.snapRing.remove?.();
    this.snapRing = null;
  }

  /**
   * 更新已绘制边的长度标注
   * @param points 已确认顶点
   * @param cursor 当前光标坐标（可选，用于显示最后一条动态边）
   */
  updateEdgeLabels(points: [number, number][], cursor?: [number, number]) {
    this.clearEdgeLabels();

    const hasAnySegment = points.length >= 2 || (points.length === 1 && cursor);
    if (!hasAnySegment) return;

    type Point = [number, number];
    const segments: Array<[Point, Point]> = [];

    for (let i = 0; i < points.length - 1; i++) {
      segments.push([points[i], points[i + 1]]);
    }

    if (cursor) {
      segments.push([points[points.length - 1], cursor]);
    }

    segments.forEach(([a, b]) => {
      const distance = this.map.getDistance(toBdPoint(a), toBdPoint(b));
      const text = distance < 1000
        ? `${distance.toFixed(1)} m`
        : `${(distance / 1000).toFixed(2)} km`;

      const mid: [number, number] = [
        (a[0] + b[0]) / 2,
        (a[1] + b[1]) / 2,
      ];

      const label = new window.BMapGL.Label(text, {
        position: toBdPoint(mid),
        offset: new window.BMapGL.Size(0, -15),
      });
      label.setStyle(DRAWING_EDGE_LABEL_STYLE);
      this.map.addOverlay(label);

      this.edgeLabels.push({
        overlay: label,
        remove: () => this.map.removeOverlay(label),
      });
    });
  }

  /** 清除所有边长 Label */
  clearEdgeLabels() {
    this.edgeLabels.forEach((label) => label.remove?.());
    this.edgeLabels = [];
  }

  /** 清除所有绘制视觉元素 */
  clear() {
    if (this.templine) {
      this.map.removeOverlay(this.templine);
      this.templine = null;
    }
    this.clearHintline();
    this.clearPreviewPolygon();
    this.markers.forEach((m) => m.remove?.());
    this.markers = [];
    this.hideSnapRing();
    this.clearEdgeLabels();
  }
}
